import type { ReactNode } from 'react';

/**
 * The timer ring (4a/8a): geometry lives in a 300-unit box so the 6-unit stroke keeps its proportion at any display size.
 *
 * `drain` empties clockwise from 12 o'clock against a solid track, driven by `progress`.
 *
 * `marker` is Track mode (8a): a dotted track with one short coloured bar riding it, carried right round the circle
 * once a minute by a CSS animation. It ignores `progress` on purpose - Track has no target, so the ring says that
 * time is passing rather than how much is left, and a value that updates once a second would make it tick.
 */
const R = 140;
const CIRCUMFERENCE = 2 * Math.PI * R;
/** Design 8a: a 46-unit arc on an 880-unit circle, so roughly one twentieth of the ring. */
const ARC = 46;

type Props = {
  /** Fixed pixels, or omitted to fill the container - the landing card's ring is fluid. */
  size?: number;
  /** 0..1 */ progress: number; color: string;
  variant?: 'drain' | 'marker';
  /** Running gives `drain` its halo and keeps `marker` sweeping; paused and idle rings are still and flat. */
  running?: boolean;
  children?: ReactNode; className?: string;
};

export function ProgressRing({ size, progress, color, variant = 'drain', running = true, children, className }: Props) {
  const box = size ? { width: size, height: size } : { width: '100%', aspectRatio: '1' };
  const track = variant === 'marker' ? { strokeDasharray: '2 10', strokeLinecap: 'round' as const } : {};
  return (
    <div className={className} style={{ ...box, position: 'relative', display: 'grid', placeItems: 'center' }}>
      <svg viewBox="0 0 300 300" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
        <circle cx="150" cy="150" r={R} fill="none" stroke="var(--color-ring-track)" strokeWidth="6" {...track} />
        {variant === 'drain' && (
          // Design 4a: the arc glides over most of a second rather than snapping, and a running countdown
          // carries a soft halo of its own colour. Both are off while paused, so stillness reads as stopped.
          <circle cx="150" cy="150" r={R} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE} strokeDashoffset={CIRCUMFERENCE * progress}
            style={{
              transition: 'stroke-dashoffset .6s linear, stroke .3s, filter .3s',
              filter: running ? `drop-shadow(0 0 10px color-mix(in srgb, ${color} 45%, transparent))` : undefined,
            }} />
        )}
      </svg>
      {variant === 'marker' && (
        // One arc, carried all the way round by the animation - never positioned from the elapsed time, or it would
        // step once a second. Pausing freezes it where it stands; an idle ring rests at twelve o'clock.
        <svg viewBox="0 0 300 300" aria-hidden="true" className="animate-ring-sweep"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transformOrigin: '50% 50%', animationPlayState: running ? 'running' : 'paused' }}>
          <circle cx="150" cy="150" r={R} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${ARC} ${CIRCUMFERENCE - ARC}`} transform="rotate(-90 150 150)" style={{ transition: 'stroke .3s' }} />
        </svg>
      )}
      <div style={{ position: 'relative', textAlign: 'center' }}>{children}</div>
    </div>
  );
}
